import React from "react";
import IfsHistoryVM from "../viewmodel/IfsHistoryVM";
import FileDownloadIcon from "@mui/icons-material/FileDownload";
import FileIcon from "@mui/icons-material/InsertDriveFile";
import HomeIcon from "@mui/icons-material/Home";
import HistoryIcon from "@mui/icons-material/History";
import CBackDrop from "../../../basecomp/components/CBackDrop";
import CText from "../../../basecomp/newComponents/CText";
import CTable from "../../../basecomp/newComponents/CTable";
import CButton from "../../../basecomp/newComponents/CButton";
import CBox from "../../../basecomp/newComponents/CBox";
import CThemeProvider from "../../../basecomp/newComponents/CThemeProvider";
import { conasTheme } from "../../../basecomp/newComponents/CTheme";
import CCard from "../../../basecomp/newComponents/CCard";
import CTooltip from "../../../basecomp/newComponents/CTooltip";
import CChip from "../../../basecomp/newComponents/CChip";
import CInput from "../../../basecomp/newComponents/CInput";

const IfsHistoryView = () => {
  const {
    searchTerm,
    page,
    rowsPerPage,
    filteredRecords,
    isLoading,
    isDownloading,
    columns,
    handleSearchChange,
    handleChangePage,
    handleChangeRowsPerPage,
    handleGoHome,
    handleCloseBackdrop,
    getStatusCellConfig
  } = IfsHistoryVM();

const renderCell = (cellConfig, record) => {
  if (!cellConfig) return null;

  switch (cellConfig.type) {
    case "text":
      return cellConfig.content;
      
    case "chip":
      return (
        <CChip
          label={cellConfig.config.label}
          color={cellConfig.config.color}
          variant={cellConfig.config.variant}
        />
      );
      
    case "button":
      return (
        <CButton
          type="custom"
          onClick={cellConfig.config.onClick}
          disabled={cellConfig.config.disabled}
          size="small"
          label={cellConfig.config.label}
          sx={cellConfig.config.sx}
        />
      );
      
    case "iconButton":
      // Handle single button (no buttons array)
      if (!cellConfig.config.buttons) {
        return (
          <CBox sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
            <CTooltip title={cellConfig.config.tooltip}>
              <span>
                <CButton
                  type="icon"
                  customIcon={cellConfig.config.icon}
                  onClick={cellConfig.config.onClick}
                  size="small"
                  disabled={cellConfig.config.disabled}
                  iconButton={true}
                  sx={cellConfig.config.sx}
                />
              </span>
            </CTooltip>
          </CBox>
        );
      }
      
      // Handle multiple buttons (with buttons array)
      return (
        <CBox sx={{ display: "flex", justifyContent: "center", gap: 1 }}>
          {cellConfig.config.buttons.map((btn, idx) => (
            <CTooltip key={idx} title={btn.tooltip}>
              <span>
                <CButton
                  type="icon"
                  customIcon={<FileDownloadIcon />}
                  onClick={btn.onClick}
                  size="small"
                  disabled={btn.disabled}
                  iconButton={true}
                  sx={btn.icon === "output" ? { color: "success.main" } : {}}
                />
              </span>
            </CTooltip>
          ))}
        </CBox>
      );
      
    case "box":
      return (
        <CBox sx={cellConfig.config.sx}>
          {cellConfig.config.content}
        </CBox>
      );
      
    default:
      if (typeof cellConfig === 'string' || typeof cellConfig === 'number') {
        return cellConfig;
      }
      return null;
  }
};
  const tableColumns = columns.map(col => ({
    ...col,
    render: col.render ? (record) => {
      const cellConfig = col.render(record);
      return renderCell(cellConfig, record);
    } : undefined
  }));

  return (
    <CThemeProvider theme={conasTheme}>
      <CBackDrop 
        cOpen={isDownloading} 
        cHandleClose={handleCloseBackdrop} 
        cAllowClickToClose={false} 
      />
      <CBox sx={{ 
        width: "100%", 
        mt: "100px", 
        display: "flex", 
        flexDirection: "column", 
        alignItems: "center" 
      }}>
        <CBox sx={{ 
          width: "100%", 
          maxWidth: "1920px", 
          px: { xs: 2, sm: 3, md: 4 }, 
          boxSizing: "border-box" 
        }}>
          {/* Header Section */}
          <CBox sx={{ 
            display: "flex", 
            justifyContent: "space-between", 
            alignItems: "center", 
            mb: { xs: 1, sm: 1.5 } 
          }}>
            <CText
              variant="h5"
              strong={true}
              icon={<HistoryIcon />}
              sx={{ 
                color: conasTheme.palette.primary.main, 
                marginLeft: "20px", 
                fontSize: { xs: "1.2rem", sm: "1.5rem" } 
              }}
            >
              IFS Operations History
            </CText>
            <CButton
              type="custom"
              variant="outlined"
              color="primary"
              size="medium"
              customIcon={<HomeIcon />}
              iconPosition="start"
              onClick={handleGoHome}
              label="Home"
              sx={{ marginRight: "50px" }}
            />
          </CBox>

          {/* Main Content Card */}
          <CCard
            cards={[
              {
                id: "processed-records",
                header: "",
                value: "",
                content: (
                  <>
                    <CBox sx={{ 
                      display: "flex", 
                      flexDirection: { xs: "column", sm: "row" }, 
                      justifyContent: "space-between", 
                      alignItems: { xs: "stretch", sm: "center" }, 
                      mb: 3, 
                      gap: { xs: 2, sm: 0 } 
                    }}>
                      <CText
                        variant="h6"
                        icon={<FileIcon />}
                        sx={{ 
                          fontSize: { xs: "1rem", sm: "1.25rem" }, 
                          flexShrink: 0 
                        }}
                      >
                        Processed Records
                      </CText>
                      <CInput
                        placeholder="Search..."
                        value={searchTerm}
                        onChange={handleSearchChange}
                        size="small"
                        variant="outlined"
                        fullWidth={true}
                        sx={{ 
                          width: { xs: "100%", sm: "auto" }, 
                          minWidth: { xs: "100%", sm: 250 } 
                        }}
                      />
                    </CBox>
                    <CBox sx={{ overflowX: "auto", width: "100%" }}>
                      <CTable
                        columns={tableColumns}
                        data={filteredRecords}
                        loading={isLoading}
                        showSearch={false}
                        page={page}
                        rowsPerPage={rowsPerPage}
                        onPageChange={handleChangePage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        defaultRowsPerPage={25}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        emptyMessage="No records found"
                        tableProps={{
                          size: "small",
                          "aria-label": "IFS operations history table",
                          sx: {
                            minWidth: "100%",
                            "& .MuiTableCell-root": {
                              whiteSpace: "nowrap",
                              padding: { xs: "8px 6px", sm: "16px 8px" }
                            },
                            "& .MuiTableCell-head": {
                              fontSize: { xs: "0.75rem", sm: "0.875rem" }
                            }
                          }
                        }}
                      />
                    </CBox>
                  </>
                )
              }
            ]}
            sx={{ 
              p: { xs: 1.5, sm: 2, md: 3 }, 
              pt: { xs: 1, sm: 1.5, md: 2 }, 
              borderRadius: 2, 
              bgcolor: conasTheme.palette.background.paper, 
              width: "100%", 
              boxSizing: "border-box", 
              maxWidth: "100%", 
            }}
            fullWidth={true}
            elevation={3}
          />
        </CBox>
      </CBox>
    </CThemeProvider>
  );
};

export default IfsHistoryView;